import { HttpErrorResponse } from "@angular/common/http";
import {
  Component,
  DestroyRef,
  EventEmitter,
  OnInit,
  Output,
  computed,
  signal
} from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { FormControl, FormGroup } from "@angular/forms";
import { Option } from "@interfaces/option";
import {
  PARAMETRIZATION_GROUP_LABELS,
  PARAMETRIZATION_SEVERITY_LABELS,
  ParametrizationIssue,
  ParametrizationIssueGroup,
  ParametrizationIssueSeverity,
  ParametrizationIssuesResponse
} from "@interfaces/parametrization-issue";
import { Store } from "@ngrx/store";
import { CompanyService } from "@services/company.service";
import { selectPNCompany } from "@state/selectors/user.selectors";
import { Subscription } from "rxjs";

type GroupFilter = ParametrizationIssueGroup | "";
type SeverityFilter = ParametrizationIssueSeverity | "";

@Component({
  selector: "app-parametrization-issues-tab",
  templateUrl: "./parametrization-issues-tab.component.html",
  styleUrls: ["./parametrization-issues-tab.component.scss"]
})
export class ParametrizationIssuesTabComponent implements OnInit {
  /** Emitido a cada verificação concluída (usado para o contador na aba). */
  @Output() checked = new EventEmitter<ParametrizationIssuesResponse>();
  /** Emitido ao clicar em uma pendência, para abrir a aba correspondente. */
  @Output() navigate = new EventEmitter<ParametrizationIssueGroup>();

  result = signal<ParametrizationIssuesResponse | null>(null);
  loading = signal(false);
  error = signal<string | null>(null);

  company = this.store.selectSignal(selectPNCompany);

  groupLabels = PARAMETRIZATION_GROUP_LABELS;
  severityLabels = PARAMETRIZATION_SEVERITY_LABELS;

  groupOptions: Option<GroupFilter>[] = [
    { display: "Todas", value: "" },
    { display: PARAMETRIZATION_GROUP_LABELS.CADASTRO, value: "CADASTRO" },
    {
      display: PARAMETRIZATION_GROUP_LABELS.CERTIFICADO,
      value: "CERTIFICADO"
    },
    { display: PARAMETRIZATION_GROUP_LABELS.NFE, value: "NFE" },
    { display: PARAMETRIZATION_GROUP_LABELS.NFCE, value: "NFCE" },
    { display: PARAMETRIZATION_GROUP_LABELS.NFSE, value: "NFSE" }
  ];

  severityOptions: Option<SeverityFilter>[] = [
    { display: "Todas", value: "" },
    { display: PARAMETRIZATION_SEVERITY_LABELS.ERRO, value: "ERRO" },
    { display: PARAMETRIZATION_SEVERITY_LABELS.AVISO, value: "AVISO" }
  ];

  filters = new FormGroup({
    grupo: new FormControl<GroupFilter>("", { nonNullable: true }),
    severidade: new FormControl<SeverityFilter>("", { nonNullable: true })
  });

  private filterValues = toSignal(this.filters.valueChanges, {
    initialValue: this.filters.getRawValue()
  });

  filtered = computed<ParametrizationIssue[]>(() => {
    const issues = this.result()?.pendencias ?? [];
    const { grupo, severidade } = this.filterValues();

    return issues.filter(
      (issue) =>
        (!grupo || issue.grupo === grupo) &&
        (!severidade || issue.severidade === severidade)
    );
  });

  hasActiveFilter = computed(() => {
    const { grupo, severidade } = this.filterValues();
    return !!grupo || !!severidade;
  });

  sub$: Subscription[] = [];

  constructor(
    private readonly store: Store,
    private readonly companyService: CompanyService,
    private readonly destroyRef: DestroyRef
  ) {
    this.destroyRef.onDestroy(() => {
      this.sub$.forEach((sub$) => sub$.unsubscribe());
    });
  }

  ngOnInit(): void {
    queueMicrotask(() => {
      this.verify();
    });
  }

  verify() {
    if (this.loading()) return;

    this.loading.set(true);
    this.error.set(null);

    this.sub$.push(
      this.companyService.fetchParametrizationIssues().subscribe({
        next: (response) => {
          this.result.set(response);
          this.loading.set(false);
          this.checked.emit(response);
        },
        error: (err: HttpErrorResponse) => {
          this.loading.set(false);
          this.error.set(
            err?.error?.message?.message ??
              err?.error?.message ??
              "Não foi possível verificar as pendências. Tente novamente."
          );
        }
      })
    );
  }

  clearFilters() {
    this.filters.reset();
  }

  open(issue: ParametrizationIssue) {
    this.navigate.emit(issue.grupo);
  }

  trackByIssue(index: number, issue: ParametrizationIssue) {
    return `${issue.grupo}|${issue.secao}|${issue.campo}|${index}`;
  }
}
